import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, euro } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, AlertCircle, Sparkles } from "lucide-react";

const emptyForm = { name: "", category: "Gelnagels", price: 0, duration_minutes: "", description: "", active: true, sort_order: 99 };
const CATS = ["Gelnagels", "Bijwerking – eigen werk", "Verwijdering", "Nail art"];

export default function Treatments() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => { setLoading(true); api.get("/services/all").then(({ data }) => setServices(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const missingDuration = services.some((s) => !s.duration_minutes);

  return (
    <div data-testid="admin-treatments">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-4xl font-semibold text-brand-charcoal">Behandelingen</h1>
        <button onClick={() => setEditing(emptyForm)} className="flex items-center gap-2 bg-brand-charcoal text-white font-semibold px-5 py-2.5 rounded-full text-sm" data-testid="add-treatment-button">
          <Plus size={16} /> Behandeling toevoegen
        </button>
      </div>

      {missingDuration && (
        <div className="flex items-start gap-2.5 bg-brand-pink/15 border border-brand-pink/30 rounded-2xl p-4 mt-5 text-sm text-brand-charcoal">
          <AlertCircle size={18} className="text-brand-pinkhover mt-0.5 shrink-0" />
          Sommige behandelingen hebben nog geen duur ingesteld. Een behandeling is pas online boekbaar zodra je een duur invult.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-pink" size={28} /></div>
      ) : (
        <div className="space-y-3 mt-6">
          {services.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-brand-border p-4 flex items-center justify-between gap-4" data-testid={`treatment-row-${s.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="eyebrow">{s.category}</span>
                  {!s.active && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Inactief</span>}
                  {!s.duration_minutes && <span className="text-xs bg-brand-pink/20 text-brand-pinkhover px-2 py-0.5 rounded-full">Duur niet ingesteld</span>}
                </div>
                <p className="font-medium text-brand-charcoal mt-1">{s.name}</p>
                <p className="text-sm text-brand-muted">{euro(s.price)} · {s.duration_minutes ? `${s.duration_minutes} min` : "— min"}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setEditing(s)} className="p-2 rounded-lg hover:bg-brand-blush text-brand-charcoal" data-testid={`edit-treatment-${s.id}`}><Pencil size={17} /></button>
                <button onClick={async () => { if (window.confirm("Verwijderen?")) { await api.delete(`/admin/services/${s.id}`); toast.success("Verwijderd"); load(); } }} className="p-2 rounded-lg hover:bg-red-50 text-red-400" data-testid={`delete-treatment-${s.id}`}><Trash2 size={17} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddonsSection />

      {editing && <EditModal service={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AddonsSection() {
  const [addons, setAddons] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", price: 0, additional_duration: "", active: true };

  const load = () => api.get("/admin/addons").then(({ data }) => setAddons(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      name: editing.name, price: parseFloat(editing.price) || 0,
      additional_duration: editing.additional_duration === "" ? null : parseInt(editing.additional_duration),
      active: editing.active,
    };
    try {
      if (editing.id) await api.put(`/admin/addons/${editing.id}`, payload);
      else await api.post("/admin/addons", payload);
      toast.success("Opgeslagen");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-12" data-testid="admin-addons">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-medium text-brand-charcoal flex items-center gap-2"><Sparkles size={18} className="text-brand-pink" /> Extra's (add-ons)</h2>
          <p className="text-brand-muted text-sm mt-1">Deze verschijnen als optionele extra's ná het kiezen van een behandeling, niet als aparte behandeling.</p>
        </div>
        <button onClick={() => setEditing(empty)} className="flex items-center gap-2 bg-brand-charcoal text-white font-semibold px-5 py-2.5 rounded-full text-sm" data-testid="add-addon-button"><Plus size={16} /> Extra toevoegen</button>
      </div>

      <div className="space-y-3 mt-5">
        {addons.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-brand-border p-4 flex items-center justify-between gap-4" data-testid={`addon-row-${a.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-brand-charcoal">{a.name}</p>
                {!a.active && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Inactief</span>}
              </div>
              <p className="text-sm text-brand-muted">{euro(a.price)} per set · {a.additional_duration ? `+${a.additional_duration} min` : "geen extra tijd"}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => setEditing({ ...a, additional_duration: a.additional_duration ?? "" })} className="p-2 rounded-lg hover:bg-brand-blush text-brand-charcoal" data-testid={`edit-addon-${a.id}`}><Pencil size={17} /></button>
              <button onClick={async () => { if (window.confirm("Verwijderen?")) { await api.delete(`/admin/addons/${a.id}`); toast.success("Verwijderd"); load(); } }} className="p-2 rounded-lg hover:bg-red-50 text-red-400" data-testid={`delete-addon-${a.id}`}><Trash2 size={17} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setEditing(null)} data-testid="addon-modal">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-semibold text-brand-charcoal">{editing.id ? "Extra bewerken" : "Nieuwe extra"}</h2>
              <button onClick={() => setEditing(null)}><X size={20} className="text-brand-muted" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="block text-sm font-medium text-brand-charcoal mb-1.5">Naam</label><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none" data-testid="addon-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-brand-charcoal mb-1.5">Prijs (€)</label><input type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none" data-testid="addon-price" /></div>
                <div><label className="block text-sm font-medium text-brand-charcoal mb-1.5">Extra tijd (min)</label><input type="number" placeholder="optioneel" value={editing.additional_duration} onChange={(e) => setEditing({ ...editing, additional_duration: e.target.value })} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none" data-testid="addon-duration" /></div>
              </div>
              <label className="flex items-center gap-2.5 cursor-pointer pt-1">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-5 w-5 accent-brand-pinkhover" data-testid="addon-active" />
                <span className="text-sm font-medium text-brand-charcoal">Actief (zichtbaar bij boeken)</span>
              </label>
            </div>
            <button disabled={!editing.name} onClick={save} className="w-full mt-5 bg-brand-charcoal text-white font-semibold py-3 rounded-full disabled:opacity-40" data-testid="addon-save">Opslaan</button>
          </div>
        </div>
      )}
    </div>
  );
}

function EditModal({ service, onClose, onSaved }) {
  const isNew = !service.id;
  const [form, setForm] = useState({ ...service, duration_minutes: service.duration_minutes ?? "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const payload = {
      name: form.name, category: form.category, price: parseFloat(form.price) || 0,
      duration_minutes: form.duration_minutes === "" ? null : parseInt(form.duration_minutes),
      description: form.description || "", active: form.active, sort_order: parseInt(form.sort_order) || 99,
    };
    try {
      if (isNew) await api.post("/admin/services", payload);
      else await api.put(`/admin/services/${service.id}`, payload);
      toast.success(isNew ? "Behandeling toegevoegd" : "Opgeslagen");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="treatment-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-semibold text-brand-charcoal">{isNew ? "Nieuwe behandeling" : "Behandeling bewerken"}</h2>
          <button onClick={onClose}><X size={20} className="text-brand-muted" /></button>
        </div>
        <div className="space-y-3">
          <L label="Naam"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" data-testid="treatment-name" /></L>
          <L label="Categorie">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="inp" data-testid="treatment-category">
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Prijs (€)"><input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="inp" data-testid="treatment-price" /></L>
            <L label="Duur (min)"><input type="number" placeholder="bv. 60" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="inp" data-testid="treatment-duration" /></L>
          </div>
          <L label="Beschrijving"><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="inp resize-none" data-testid="treatment-description" /></L>
          <label className="flex items-center gap-2.5 cursor-pointer pt-1">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-5 w-5 accent-brand-pinkhover" data-testid="treatment-active" />
            <span className="text-sm font-medium text-brand-charcoal">Actief (zichtbaar op website)</span>
          </label>
        </div>
        <button disabled={saving || !form.name} onClick={save} className="w-full mt-5 bg-brand-charcoal text-white font-semibold py-3 rounded-full disabled:opacity-40" data-testid="treatment-save">
          {saving ? "Bezig…" : "Opslaan"}
        </button>
      </div>
      <style>{`.inp{width:100%;border:1px solid var(--soft-border);border-radius:0.9rem;padding:0.6rem 0.9rem;font-size:0.9rem;outline:none}.inp:focus{border-color:var(--pink)}`}</style>
    </div>
  );
}

function L({ label, children }) {
  return <div><label className="block text-sm font-medium text-brand-charcoal mb-1.5">{label}</label>{children}</div>;
}
